import type { SagaReturnType } from 'redux-saga/effects';
import { put, takeEvery, delay, call } from 'redux-saga/effects';
import { api } from 'src/api';
import { closestDatesStartSearchAsyncAction } from './actions';
import { setDataAction, setStatusAction } from './slice';
import { searchDoneAnalytics } from './analytics';

/**
 * Сага-воркер для обработки поиска ближайших дат.
 *
 * Эта генераторная функция инициирует процесс поиска, отправляя действие для установки статуса в 'pending'.
 * Затем она входит в цикл, где повторно вызывает API `api.getSearchClosestDates` до тех пор, пока поиск не будет завершен.
 * Если поиск все еще в ожидании, она ждет 1 секунду перед следующим вызовом API.
 * Если во время вызова API происходит ошибка, она логирует ошибку и устанавливает статус в 'error'.
 *
 * @param action - Действие, которое инициирует поиск, содержащее идентификатор запроса.
 */
function* startSearchWorker(
  action: ReturnType<typeof closestDatesStartSearchAsyncAction>,
) {
  yield put(setStatusAction('pending'));
  let isSearchPending = true;
  while (isSearchPending) {
    try {
      const response: SagaReturnType<typeof api.getSearchClosestDates> =
        yield api.getSearchClosestDates({ request_id: action.payload });
      yield put(setDataAction(response.result));
      yield put(setStatusAction(response.status));

      isSearchPending = response.status === 'pending';
      if (isSearchPending) {
        yield delay(1000);
      }

      if (response.status !== 'pending') {
        yield call(searchDoneAnalytics, response.status);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error occurred during search:', error);
      yield put(setStatusAction('error'));
      isSearchPending = false;
    }
  }
}

export default function* closestDatesWatcher() {
  yield takeEvery(closestDatesStartSearchAsyncAction, startSearchWorker);
}
